import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { onAppVisible } from '../lib/platform'
import { useLocation } from 'react-router-dom'
import { fetchNotifications, fetchUnreadNotificationCount, markAllNotificationsRead, type NotificationPage, type UserNotification } from '../lib/api'

export function NotificationBell() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const query = useInfiniteQuery({
    queryKey: ['user-notifications'],
    queryFn: ({ pageParam }) => fetchNotifications(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: page => page.next ?? undefined,
    staleTime: 30000,
    refetchInterval: 60000,
  })
  const unreadQuery = useQuery({
    queryKey: ['user-notifications-unread-count'],
    queryFn: fetchUnreadNotificationCount,
    staleTime: 30000,
    refetchInterval: 60000,
  })
  const notifications = query.data?.pages.flatMap(page => page.results) ?? []
  const unreadCount = unreadQuery.data ?? 0
  const queryClient = useQueryClient()
  const markedWhileOpen = useRef(false)

  useEffect(() => { setOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!open) {
      markedWhileOpen.current = false
      return
    }
    if (unreadCount === 0 || markedWhileOpen.current) return
    markedWhileOpen.current = true
    const readAt = new Date().toISOString()
    queryClient.setQueryData<InfiniteData<NotificationPage>>(['user-notifications'], current => current && ({
      ...current,
      pages: current.pages.map(page => ({ ...page, results: page.results.map(item => item.is_read ? item : { ...item, is_read: true, read_at: item.read_at ?? readAt }) })),
    }))
    queryClient.setQueryData<number>(['user-notifications-unread-count'], 0)
    void markAllNotificationsRead().catch(() => {
      void queryClient.invalidateQueries({ queryKey: ['user-notifications'] })
      void queryClient.invalidateQueries({ queryKey: ['user-notifications-unread-count'] })
    })
  }, [open, unreadCount, queryClient])
  const refreshNotifications = query.refetch
  const refreshUnreadCount = unreadQuery.refetch
  useEffect(() => onAppVisible(() => { void refreshNotifications(); void refreshUnreadCount() }), [refreshNotifications, refreshUnreadCount])

  useEffect(() => {
    if (!open) return
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  useEffect(() => {
    const target = loadMoreRef.current
    if (!open || !target || !query.hasNextPage) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !query.isFetchingNextPage) void query.fetchNextPage()
    }, { root: target.closest('.pwa-notification-list'), rootMargin: '100px' })
    observer.observe(target)
    return () => observer.disconnect()
  }, [open, query.fetchNextPage, query.hasNextPage, query.isFetchingNextPage, notifications.length])

  return <div className="pwa-notification" ref={rootRef}>
    <button className="pwa-icon-button pwa-notification-button" type="button" onClick={() => setOpen(value => !value)} aria-label="Notificações" aria-haspopup="dialog" aria-expanded={open}>
      <BellIcon />
      {unreadCount > 0 && <span className="pwa-notification-badge" aria-label={`${unreadCount} notificações não lidas`}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
    </button>
    {open && <section className="pwa-notification-panel" role="dialog" aria-label="Notificações">
      <header><h2>Notificações</h2><small>{unreadCount > 0 ? `${unreadCount} não ${unreadCount === 1 ? 'lida' : 'lidas'}` : 'Tudo em dia'}</small></header>
      <div className="pwa-notification-list">
        {query.isLoading && <p className="pwa-notification-state">Carregando notificações…</p>}
        {query.isError && <div className="pwa-notification-state"><p>Não foi possível carregar as notificações.</p><button type="button" onClick={() => void query.refetch()}>Tentar novamente</button></div>}
        {!query.isLoading && !query.isError && notifications.length === 0 && <p className="pwa-notification-state">Nenhuma notificação.</p>}
        {notifications.map(notification => <NotificationItem key={notification.code} notification={notification} />)}
        <div ref={loadMoreRef} className="pwa-notification-loader" aria-live="polite">{query.isFetchingNextPage ? 'Carregando mais…' : ''}</div>
      </div>
    </section>}
  </div>
}

function notificationTitle(notification: UserNotification, language: string) {
  if (notification.notification_type === 'new_delivery') return newDeliveryTitle(language)
  return notification.notification_type_display || notification.subject || 'Notificação'
}

function newDeliveryTitle(language: string) {
  const normalized = language.toLowerCase()
  if (normalized.startsWith('en')) return 'New Delivery'
  if (normalized.startsWith('es')) return 'Nueva entrega'
  return 'Nova entrega'
}

function NotificationItem({ notification }: { notification: UserNotification }) {
  const { i18n } = useTranslation()
  return <article className={`pwa-notification-item${notification.is_read ? '' : ' is-unread'}`}>
    <span className="pwa-notification-item-icon"><BellIcon /></span>
    <span><strong>{notificationTitle(notification, i18n.language)}</strong><p>{notification.message}</p><small>{formatDate(notification.created_at)}</small></span>
  </article>
}

function BellIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></svg>
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(date)
}
